import {
  AddressAutocomplete,
  AddressAutocompleteOnSelectValues,
  FormInput,
  genericMemo,
  HelperTxt,
  NewSelect,
  SelectOptionType,
} from "@chhjit/react-components";
import { Box, Collapse, FormControl, Grid, Link } from "@material-ui/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { Address } from "@hunkware/shared/types";

import { useFieldConfig } from "../../../context/step-config-context";
import { State } from "../../../types";
import { getSelectValue, loadGoogleMaps } from "../../../utils";
import { useAddressFieldsStyles } from "./styles";

const setValueParams = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

/**
 * Derives sub-field names from the composite address field's baseName.
 *
 * Legacy (baseName = "address" or empty): uses original unprefixed names
 *   address, address2, city, state, postal, country
 *
 * New prefixed (e.g. baseName = "origin" or "destination"):
 *   origin, origin2, originCity, originState, originPostal, originCountry
 */
const getSubFieldNames = (baseName: string) => {
  if (!baseName || baseName === "address") {
    return {
      main: "address",
      line2: "address2",
      city: "city",
      state: "state",
      postal: "postal",
      country: "country",
    };
  }
  return {
    main: baseName,
    line2: `${baseName}2`,
    city: `${baseName}City`,
    state: `${baseName}State`,
    postal: `${baseName}Postal`,
    country: `${baseName}Country`,
  };
};

export const Addresses = ({
  states,
  statesLoading,
  baseName = "address",
}: {
  states: State[];
  statesLoading?: boolean;
  baseName?: string;
}) => {
  const styles = useAddressFieldsStyles();
  const { getLabel, getTextBlock } = useFieldConfig();

  const fields = useMemo(() => getSubFieldNames(baseName), [baseName]);

  const statesOptions = useMemo(() => {
    const sortedByCountry: {
      label: string;
      options: { label: string; value: string }[];
    }[] = [];

    states.forEach((state) => {
      const countryIndex = sortedByCountry.findIndex(
        ({ label }) => label === state.countryFull,
      );

      const option = {
        label: state.stateFull,
        value: state.state,
      };

      if (countryIndex > -1) {
        sortedByCountry[countryIndex].options.push(option);
      } else {
        sortedByCountry.push({
          label: state.countryFull,
          options: [option],
        });
      }
    });

    return sortedByCountry;
  }, [states]);

  const {
    control,
    formState: { dirtyFields, errors },
    setValue,
    trigger,
    watch,
  } = useFormContext();

  const [mainAddress, address2, city, state, postal, country] = watch([
    fields.main,
    fields.line2,
    fields.city,
    fields.state,
    fields.postal,
    fields.country,
  ]);

  // Persist "manual mode" per address field so autocomplete view survives navigation.
  // Only set to true when user explicitly clicks "I can't find my address".
  const storageKey = `booking-v4-address-manual-${baseName}`;
  const [showAddressFields, setShowAddressFields] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(storageKey) === "true";
    } catch {
      return false;
    }
  });
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState<boolean>(false);

  // Load Google Maps API only when AddressAutocomplete is needed (when !showAddressFields)
  useEffect(() => {
    if (!showAddressFields && !googleMapsLoaded) {
      loadGoogleMaps()
        .then(() => {
          setGoogleMapsLoaded(true);
        })
        .catch((error) => {
          console.error("Failed to load Google Maps API:", error);
          // Fallback: show manual address fields if Google Maps fails to load
          setShowAddressFields(true);
        });
    }
  }, [showAddressFields, googleMapsLoaded]);

  const isAddressTouched = useMemo(
    () =>
      (dirtyFields as Record<string, unknown>)?.[fields.main] ||
      (dirtyFields as Record<string, unknown>)?.[fields.city] ||
      (dirtyFields as Record<string, unknown>)?.[fields.country] ||
      (dirtyFields as Record<string, unknown>)?.[fields.postal] ||
      (dirtyFields as Record<string, unknown>)?.[fields.state],
    [
      dirtyFields,
      fields.main,
      fields.city,
      fields.country,
      fields.postal,
      fields.state,
    ],
  );

  const isAddressBroken = useMemo(() => {
    const addressArray = [mainAddress, city, country, postal, state];

    return addressArray.some((value) => !value || value === "");
  }, [mainAddress, city, country, postal, state]);

  const fullAddress = useMemo(() => {
    const addressArray = [mainAddress, address2, city, country, postal, state];

    return addressArray.reduce((acc, curr) => {
      if (curr && curr !== "") {
        return acc === "" ? curr : acc + ", " + curr;
      }

      return acc;
    }, "");
  }, [mainAddress, address2, city, country, postal, state]);

  const setAddress = useCallback(
    (data: Address) => {
      setValue(fields.main, data.address || "", setValueParams);
      setValue(fields.line2, data.address2 || "", setValueParams);
      setValue(fields.country, data.country || "", setValueParams);
      setValue(fields.city, data.city || "", setValueParams);
      setValue(fields.state, data.state || "", setValueParams);
      setValue(fields.postal, data.postal || "", setValueParams);
    },
    [setValue, fields],
  );

  const onAddressAutocomplete = useCallback(
    (data: AddressAutocompleteOnSelectValues) => {
      if (data && data.fullAddress) {
        setAddress(data.fullAddress as Address);
      }
    },
    [setAddress],
  );

  // On mount: if sessionStorage says manual mode but there's no address data,
  // reset to autocomplete (handles new booking session after previous session).
  useEffect(() => {
    if (showAddressFields && !mainAddress && !city && !state && !postal) {
      setShowAddressFields(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist manual mode to sessionStorage when it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, String(showAddressFields));
    } catch {
      // ignore
    }
  }, [showAddressFields, storageKey]);

  // If the address is incomplete (broken) after the user has interacted,
  // switch to manual mode so they can fill in the missing fields.
  useEffect(() => {
    if (isAddressTouched && isAddressBroken) {
      setShowAddressFields(true);
    }
  }, [isAddressBroken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect browser autofill and trigger validation
  useEffect(() => {
    const handleAutofill = () => {
      // Trigger validation for all address fields when autofill is detected
      trigger([
        fields.main,
        fields.line2,
        fields.city,
        fields.state,
        fields.postal,
        fields.country,
      ]);
    };

    // Listen for animationstart event (Chrome/Safari fire this on autofill)
    const inputs = document.querySelectorAll(
      'input[type="text"], input[type="tel"]',
    );
    const autofillHandler = (e: AnimationEvent) => {
      if (
        e.animationName === "onAutoFillStart" ||
        e.animationName === "onAutoFillCancel"
      ) {
        // Small delay to ensure values are set
        setTimeout(handleAutofill, 100);
      }
    };

    inputs.forEach((input) => {
      input.addEventListener(
        "animationstart",
        autofillHandler as EventListener,
      );
    });

    // Also use MutationObserver as a fallback for browsers that don't fire animation events
    const observer = new MutationObserver(() => {
      // Check if any input has been autofilled by checking for the autofill pseudo-class
      const autofilledInputs = document.querySelectorAll(
        "input:-webkit-autofill",
      );
      if (autofilledInputs.length > 0) {
        setTimeout(handleAutofill, 100);
      }
    });

    // Observe the form container for changes
    const formContainer =
      document.querySelector('[id^="ob-form"]')?.closest("form") ||
      document.body;
    observer.observe(formContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["value"],
    });

    return () => {
      inputs.forEach((input) => {
        input.removeEventListener(
          "animationstart",
          autofillHandler as EventListener,
        );
      });
      observer.disconnect();
    };
  }, [trigger, fields]);

  const handleStateChange = useCallback(
    (option: SelectOptionType<string> | null) => {
      if (option?.value && option.value !== "") {
        const countryOfState = states.find(
          (item) => item.state === option.value,
        );

        setValue(fields.state, option.value);

        if (countryOfState) {
          setValue(fields.country, countryOfState.country);
        }
      }
    },
    [setValue, states, fields],
  );

  const rootElement = useMemo(() => document.getElementById("root"), []);

  const tabIndex = useMemo(() => {
    return showAddressFields ? undefined : -1;
  }, [showAddressFields]);

  return (
    <Grid container spacing={2}>
      {!showAddressFields && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                {googleMapsLoaded ? (
                  <AddressAutocomplete
                    id="ob-form-address-autocomplete"
                    label={getLabel("address", "Address")}
                    name={fields.main}
                    onSelect={onAddressAutocomplete}
                    inputValue={fullAddress}
                    checkFullness
                    withSearchIcon={false}
                    error={isAddressTouched && isAddressBroken}
                    autoComplete="off"
                    aria-label="Address"
                    popperModifiers={{ flip: { enabled: false } }}
                    popperDisablePortal={false}
                  />
                ) : (
                  <FormInput
                    id="ob-form-address-loading"
                    control={control}
                    name={fields.main}
                    label={getLabel("address", "Address")}
                    disabled
                    loading
                    aria-label="Address"
                  />
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box textAlign={{ sm: "center" }}>
                <Link
                  id="ob-form-address-not-found"
                  component="button"
                  variant="body2"
                  underline="always"
                  onClick={() => setShowAddressFields(true)}
                  className={styles.showAddressFields}
                >
                  {getTextBlock("cantFindAddress", "I can't find my address")}
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        style={{ display: showAddressFields ? "initial" : "none" }}
      >
        <FormInput
          id="ob-form-address"
          control={control}
          name={fields.main}
          label={getLabel("address", "Address")}
          required
          aria-label={getLabel("address", "Address")}
        />
      </Grid>

      <Grid item xs={12}>
        <FormInput
          id="ob-form-address2"
          control={control}
          name={fields.line2}
          label={getLabel("address2", "Apartment/suite")}
          aria-label={getLabel("address2", "Apartment/suite")}
        />
      </Grid>

      <Grid item xs={12}>
        <Collapse in={showAddressFields}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <FormInput
                id="ob-form-city"
                control={control}
                name={fields.city}
                label={getLabel("city", "City")}
                required
                tabIndex={tabIndex}
                aria-label="City"
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <FormControl
                error={!!(errors as Record<string, unknown>)[fields.state]}
                fullWidth
              >
                <NewSelect
                  id="ob-form-state"
                  value={getSelectValue<string>(state || null, statesOptions)}
                  name={fields.state}
                  required
                  label={getLabel("state", "State/province")}
                  virtualize={false}
                  blurInputOnSelect
                  options={statesOptions as any}
                  isDisabled={statesLoading}
                  onChange={handleStateChange}
                  menuPortalTarget={showAddressFields ? rootElement : undefined}
                  tabIndex={tabIndex?.toString()}
                  aria-label="State/province"
                  aria-disabled={statesLoading}
                />

                {
                  <HelperTxt>
                    {(errors as Record<string, unknown>)[fields.state]
                      ? "State is required"
                      : "*required"}
                  </HelperTxt>
                }
              </FormControl>
            </Grid>

            <Grid item xs={6} md={3}>
              <FormInput
                id="ob-form-postal"
                control={control}
                name={fields.postal}
                label={getLabel("postal", "Zip/postal code")}
                required
                tabIndex={tabIndex}
                aria-label="Zip/postal code"
              />

              <FormInput
                id="ob-form-country"
                control={control}
                name={fields.country}
                type="hidden"
                required
              />
            </Grid>
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
};

export const AddressFields = genericMemo(Addresses);
